<!-- ============================================================
  CoachLeave.vue — หน้าเดียว 2 โหมด (ผ่าน prop approval-mode ใน App.vue เหมือน GroupingSheet):

    • approval-mode=false → "ยื่นใบลา" (state/menukey: coachleave) — พนักงานทุกคน:
      การ์ดโควต้าปีนี้ (เน้นวันคงเหลือ), ปุ่มยื่นใบลา, ประวัติใบลาของตัวเอง + ยกเลิกใบที่รออนุมัติ
    • approval-mode=true → "อนุมัติการลา" (state/menukey: leaveapproval) — งานผู้จัดการ, 3 การ์ดเรียงบนลงล่าง
      ตามลำดับความเร่งด่วน:
        1. "รอดำเนินการ" — เฉพาะ pending, อนุมัติ/ปฏิเสธพร้อมหมายเหตุตรงนี้เลย (งานที่ต้องทำวันนี้)
        2. "ภาพรวมการลารายปี" — ตาราง remain/quota ต่อคน (หัก pending ไว้ล่วงหน้ากันอนุมัติเกิน), คลิกชื่อ
           กางดู panel รายละเอียด (ทุกสถานะ แต่ที่ไม่ใช่ approved แสดงจางลง — ไม่ตัดออกเพื่อให้ยอดตรงกับ
           รายการที่เห็น), footer ลิงก์กลับไปการ์ด "รอดำเนินการ" เมื่อมี pending ในมุมมองที่กรองอยู่
        3. "ประวัติที่พิจารณาแล้ว" — พับเก็บโดย default, filter สถานะ + ปุ่มยกเลิกใบที่พิจารณาแล้ว
      prop quota-editable (head) เปิดปุ่ม "กำหนดโควต้า" ในการ์ดภาพรวม. อนุมัติแล้วถ้าผู้ลาเป็นโค้ช backend
      เขียน tcoach_absence ให้อัตโนมัติ

  การลาผูกกับบัญชีล็อกอิน (tuser.username) ไม่ใช่รายชื่อโค้ช. เมนู leaveapproval default = head
  เท่านั้น (backend requireMenu('leaveapproval') เช็คซ้ำทุก endpoint — แอดมินอนุมัติไม่ได้)

  คำขอที่ข้ามปี (เช่น 30 ธ.ค.-2 ม.ค.) นับโควต้าแยกตามปีจริงที่วันลาตกอยู่ (splitLeaveDaysByYear ฝั่ง
  backend) ไม่ยัดทั้งก้อนเข้าปีเริ่มต้น — ดู server.js รอบ LEAVE_MAX_SPAN_DAYS

  Endpoints: /getMyLeaveRequests /submitLeaveRequest /cancelLeaveRequest (coachleave) ·
  /getLeaveRequests /reviewLeaveRequest /getLeaveQuotas /getStaffLeaveDetail /saveLeaveQuota(s) (leaveapproval)
============================================================ -->
<template>
  <div>
    <div class="pg-head">
      <div class="pg-ico"><span class="mdi" :class="approvalMode ? 'mdi-clipboard-check-outline' : 'mdi-calendar-clock'"></span></div>
      <div>
        <div class="id-h1">{{ approvalMode ? $t('grouping.leaveApprovalTitle') : $t('grouping.leavePageTitle') }}</div>
        <div class="pg-sub">{{ approvalMode ? $t('grouping.leaveApprovalSub') : $t('grouping.leavePageSub') }}</div>
      </div>
    </div>

    <!-- ── ส่วนของฉัน (พนักงานทุกคนที่ล็อกอินยื่นลาได้) — โหมด "ยื่นใบลา" ──────────── -->
    <div v-if="!approvalMode" class="lv-card">
      <div class="lv-cardhead">
        <span class="strong"><span class="mdi mdi-account-circle-outline"></span> {{ $t('grouping.myLeaveList') }} <span class="lv-year">{{ myYear ? displayYearOf(myYear) : '' }}</span></span>
        <button type="button" class="id-btn id-btn-primary" @click="openLeaveSubmit">
          <span class="mdi mdi-email-send-outline"></span> {{ $t('grouping.myLeaveSubmit') }}</button>
      </div>

      <!-- การ์ดโควต้า 4 ประเภท (ข้อมูลอย่างเดียว ไม่คลิกกรอง — กรองผ่านมุมมองด้านล่างแทน) -->
      <div class="lv-quota-row lv-quota-row-4">
        <div v-for="t in ['sick', 'personal', 'vacation', 'unpaid']" :key="t" class="lv-quota-card">
          <div class="lv-quota-type">{{ leaveTypeLabel(t) }}</div>
          <template v-if="t === 'unpaid'">
            <div class="lv-quota-num" style="margin-top:19px">{{ myUsed.unpaid || 0 }}<span class="lv-quota-total">{{ $t('grouping.daysUnit') }}</span></div>
            <div class="lv-quota-sub">
              {{ $t('grouping.quotaUsedLabel') }} {{ myUsed.unpaid || 0 }} {{ $t('grouping.daysUnit') }}
              <template v-if="myPending.unpaid > 0"> • {{ $t('grouping.quotaPendingNote', { n: myPending.unpaid }) }}</template>
            </div>
          </template>
          <template v-else-if="myQuota">
            <!-- เน้น "คงเหลือ" ให้พนักงานเห็นชัด: ตัวเลขใหญ่ = วันที่เหลือ, บรรทัดล่าง = ใช้ไป/เพดาน -->
            <div class="lv-quota-remainlabel">{{ $t('grouping.quotaRemainLabel') }}</div>
            <div class="lv-quota-num" :class="quotaRemainClass(t)">{{ quotaRemain(t) }}<span class="lv-quota-total">/ {{ quotaOf(myQuota, t) }} {{ $t('grouping.daysUnit') }}</span></div>
            <div class="lv-quota-bar"><span :class="quotaRemainClass(t)" :style="{ width: quotaRemainPct(t) + '%' }"></span></div>
            <div class="lv-quota-sub">
              {{ $t('grouping.quotaUsedLabel') }} {{ myUsed[t] }} {{ $t('grouping.daysUnit') }}
              <template v-if="myPending[t] > 0"> • {{ $t('grouping.quotaPendingNote', { n: myPending[t] }) }}</template>
            </div>
          </template>
          <template v-else>
            <div class="lv-quota-num">{{ myUsed[t] }}<span class="lv-quota-total">{{ $t('grouping.daysUnit') }}</span></div>
            <div class="lv-quota-sub">{{ $t('grouping.quotaNotSet') }}</div>
          </template>
        </div>
      </div>

      <div class="lv-cardhead" style="margin-top:2px">
        <span class="strong" style="font-size:13.5px">{{ $t('grouping.myLeaveHistory') }}</span>
        <div class="lv-viewtoggle">
          <button type="button" :class="{ active: myViewMode === 'grouped' }" @click="myViewMode = 'grouped'">{{ $t('grouping.viewGrouped') }}</button>
          <button type="button" :class="{ active: myViewMode === 'chrono' }" @click="myViewMode = 'chrono'">{{ $t('grouping.viewChrono') }}</button>
        </div>
      </div>

      <!-- มุมมองจัดกลุ่มตามประเภท (ค่าเริ่มต้น) -->
      <div v-if="myViewMode === 'grouped'" class="lv-grouplist id-fade-in" :key="'mygrp' + myLeaves.length">
        <div v-for="g in myGroupedByType" :key="g.type" class="lv-group">
          <div class="lv-group-head" @click="toggleMyGroup(g.type)">
            <span><span class="mdi" :class="myGroupOpen[g.type] ? 'mdi-chevron-down' : 'mdi-chevron-right'"></span> {{ leaveTypeLabel(g.type) }}</span>
            <span class="lv-group-count">{{ $t('grouping.leaveHistoryCount', { n: g.requests.length }) }}</span>
          </div>
          <div v-if="myGroupOpen[g.type]" class="lv-group-body">
            <div v-for="r in g.requests" :key="r.requestid" class="lv-row">
              <div style="flex:1;min-width:0">
                <div style="font-size:13.5px;font-weight:700">
                  <span class="badge" :class="leaveStatusClass(r.status)" style="font-size:10.5px">{{ leaveStatusLabel(r.status) }}</span></div>
                <div style="font-size:12px;color:var(--c-text-muted);overflow-wrap:anywhere">
                  {{ leaveDateLabel(r) }} ({{ leaveDays(r) }} {{ $t('grouping.daysUnit') }})<template v-if="r.dayportion && r.dayportion !== 'full'"> · {{ dayPortionLabel(r.dayportion) }}</template><template v-if="r.reason"> • {{ r.reason }}</template><a v-if="r.attachment_url" :href="r.attachment_url" target="_blank" rel="noopener" class="lv-attach" @click.stop> <span class="mdi mdi-paperclip"></span>{{ $t('grouping.leaveAttachView') }}</a>
                </div>
                <div v-if="submittedAtLabel(r)" style="font-size:11px;color:var(--c-text-muted)"><span class="mdi mdi-clock-outline"></span> {{ $t('grouping.submittedAt') }} {{ submittedAtLabel(r) }}</div>
                <div v-if="r.reviewnote" style="font-size:11.5px;color:var(--c-text-muted)">↳ {{ r.reviewnote }}</div>
              </div>
              <button v-if="r.status === 'pending'" class="id-btn id-btn-ghost" style="color:var(--c-error)" @click="askCancelLeave(r)">
                <span class="mdi mdi-close"></span> {{ $t('grouping.cancelReq') }}</button>
            </div>
          </div>
        </div>
        <div v-if="myLoaded && myGroupedByType.length === 0" class="id-empty" style="padding:18px">
          <div class="mdi mdi-inbox-outline"></div>
          <div style="margin-top:8px">{{ $t('grouping.noLeaveReq') }}</div>
        </div>
      </div>

      <!-- มุมมองเรียงตามวันที่ลา (ทุกประเภทปนกัน ล่าสุดก่อน) -->
      <div v-else class="lv-list id-fade-in" :key="'mychrono' + myLeaves.length">
        <div v-for="r in myChronological" :key="r.requestid" class="lv-row">
          <div style="flex:1;min-width:0">
            <div style="font-size:13.5px;font-weight:700">{{ leaveTypeLabel(r.leavetype) }}
              <span class="badge" :class="leaveStatusClass(r.status)" style="font-size:10.5px">{{ leaveStatusLabel(r.status) }}</span></div>
            <div style="font-size:12px;color:var(--c-text-muted);overflow-wrap:anywhere">
              {{ leaveDateLabel(r) }} ({{ leaveDays(r) }} {{ $t('grouping.daysUnit') }})<template v-if="r.dayportion && r.dayportion !== 'full'"> · {{ dayPortionLabel(r.dayportion) }}</template><template v-if="r.reason"> • {{ r.reason }}</template><a v-if="r.attachment_url" :href="r.attachment_url" target="_blank" rel="noopener" class="lv-attach" @click.stop> <span class="mdi mdi-paperclip"></span>{{ $t('grouping.leaveAttachView') }}</a>
            </div>
            <div v-if="submittedAtLabel(r)" style="font-size:11px;color:var(--c-text-muted)"><span class="mdi mdi-clock-outline"></span> {{ $t('grouping.submittedAt') }} {{ submittedAtLabel(r) }}</div>
            <div v-if="r.reviewnote" style="font-size:11.5px;color:var(--c-text-muted)">↳ {{ r.reviewnote }}</div>
          </div>
          <button v-if="r.status === 'pending'" class="id-btn id-btn-ghost" style="color:var(--c-error)" @click="askCancelLeave(r)">
            <span class="mdi mdi-close"></span> {{ $t('grouping.cancelReq') }}</button>
        </div>
        <div v-if="myLoaded && myChronological.length === 0" class="id-empty" style="padding:18px">
          <div class="mdi mdi-inbox-outline"></div>
          <div style="margin-top:8px">{{ $t('grouping.noLeaveReq') }}</div>
        </div>
      </div>
    </div>

    <!-- ── โหมด "อนุมัติการลา": รอดำเนินการ (เฉพาะ pending — งานที่ต้องทำวันนี้ ขึ้นบนสุด) ──── -->
    <div v-if="approvalMode" ref="pendingCard" class="lv-card">
      <div class="lv-cardhead">
        <span class="strong"><span class="mdi mdi-inbox-full-outline"></span> {{ $t('grouping.leavePendingTitle') }}
          <span v-if="pendingLeaveCount > 0" class="badge badge-warning" style="font-size:11px">{{ pendingLeaveCount }}</span></span>
      </div>
      <p class="lv-hint">{{ $t('grouping.leaveReqSub') }}</p>
      <div class="lv-list id-fade-in" :key="'pending' + pendingRequests.length">
        <div v-for="r in pendingRequests" :key="r.requestid" class="lv-row">
          <span class="lv-dot" :style="{ background: r.color || '#8b5cf6' }"></span>
          <div style="flex:1;min-width:0">
            <div style="font-size:13.5px;font-weight:700">{{ r.displayname }}
              <span class="badge badge-neutral" style="font-size:10.5px">{{ leaveTypeLabel(r.leavetype) }}</span></div>
            <div style="font-size:12px;color:var(--c-text-muted);overflow-wrap:anywhere">
              {{ leaveDateLabel(r) }} ({{ leaveDays(r) }} {{ $t('grouping.daysUnit') }})<template v-if="r.dayportion && r.dayportion !== 'full'"> · {{ dayPortionLabel(r.dayportion) }}</template><template v-if="r.reason"> • {{ r.reason }}</template><a v-if="r.attachment_url" :href="r.attachment_url" target="_blank" rel="noopener" class="lv-attach" @click.stop> <span class="mdi mdi-paperclip"></span>{{ $t('grouping.leaveAttachView') }}</a>
            </div>
            <div v-if="pendingQuotaContext(r)" style="font-size:11.5px;color:var(--c-text-muted)">{{ pendingQuotaContext(r) }}</div>
            <div v-if="submittedAtLabel(r)" style="font-size:11px;color:var(--c-text-muted)"><span class="mdi mdi-clock-outline"></span> {{ $t('grouping.submittedAt') }} {{ submittedAtLabel(r) }}</div>
          </div>
          <button class="id-btn id-btn-ghost" style="color:var(--c-success)" @click="askReview(r, true)"><span class="mdi mdi-check"></span> {{ $t('grouping.approve') }}</button>
          <button class="id-btn id-btn-ghost" style="color:var(--c-error)" @click="askReview(r, false)"><span class="mdi mdi-close"></span> {{ $t('grouping.reject') }}</button>
        </div>
        <div v-if="pendingRequests.length === 0" class="id-empty" style="padding:18px">
          <div class="mdi mdi-inbox-outline"></div>
          <div style="margin-top:8px">{{ $t('grouping.noLeaveReq') }}</div>
        </div>
      </div>
    </div>

    <!-- ── โหมด "อนุมัติการลา": ภาพรวมการลารายปี (ใครใช้ไป/เหลือเท่าไร ในตารางเดียว) ──── -->
    <div v-if="approvalMode" class="lv-card">
      <div class="lv-cardhead">
        <span class="strong"><span class="mdi mdi-chart-box-outline"></span> {{ $t('grouping.leaveOverviewTitle') }}
          <span class="lv-year">{{ displayYearOf(overviewYear) }}</span></span>
        <div style="display:flex;gap:6px;align-items:center">
          <button v-if="quotaEditable" type="button" class="id-btn id-btn-ghost" @click="openQuotaDialog">
            <span class="mdi mdi-tune"></span> {{ $t('grouping.quotaBtn') }}</button>
          <button type="button" class="id-btn id-btn-ghost" @click="changeOverviewYear(-1)"><span class="mdi mdi-chevron-left"></span></button>
          <button type="button" class="id-btn id-btn-ghost" @click="changeOverviewYear(1)"><span class="mdi mdi-chevron-right"></span></button>
        </div>
      </div>
      <p class="lv-hint"><span class="mdi mdi-gesture-tap"></span> {{ $t('grouping.leaveOverviewHint') }}</p>
      <p class="lv-hint"><span class="mdi mdi-information-outline"></span> {{ $t('grouping.leaveOverviewQuotaNote') }}</p>
      <div class="lv-ov-scroll">
        <table class="lv-ov">
          <thead>
            <tr>
              <th>{{ $t('grouping.staffName') }}</th>
              <th v-for="t in ['sick', 'personal', 'vacation']" :key="t">{{ leaveTypeLabel(t) }}</th>
              <th>{{ leaveTypeLabel('unpaid') }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="s in overviewStaff" :key="s.username">
              <tr class="lv-ov-row" :class="{ 'is-open': expandedUser === s.username }" @click="toggleStaffDetail(s)">
                <td class="lv-ov-name">
                  <span class="mdi lv-ov-caret" :class="expandedUser === s.username ? 'mdi-chevron-down' : 'mdi-chevron-right'"></span>
                  {{ staffName(s) }} <span class="badge badge-neutral" style="font-size:10px">{{ roleLabel(s.usertype) }}</span></td>
                <td v-for="t in ['sick', 'personal', 'vacation']" :key="t">
                  <span class="lv-ov-remain" :class="ovRemainClass(s, t)">{{ ovRemain(s, t) }}</span><span class="lv-ov-of">/ {{ ovQuota(s, t) }}</span>
                  <small class="lv-ov-used">{{ ovSubLabel(s, t) }}</small>
                </td>
                <td><span class="lv-ov-remain">{{ ovUsed(s, 'unpaid') }}</span><span class="lv-ov-of">{{ $t('grouping.daysUnit') }}</span>
                  <small class="lv-ov-used">{{ ovSubLabel(s, 'unpaid') }}</small>
                </td>
              </tr>
              <!-- แถวกางรายละเอียดใบลารายคน (เลือกกรองตามประเภทได้) -->
              <tr v-if="expandedUser === s.username" class="lv-ov-detailrow">
                <td colspan="5">
                  <div class="lv-detail">
                    <div class="lv-detail-head">
                      <span class="lv-detail-title"><span class="mdi mdi-calendar-text-outline"></span> {{ $t('grouping.leaveDetailOf', { name: staffName(s) }) }}</span>
                      <id-select v-model="detailType" :options="detailTypeOptions" style="width:170px"></id-select>
                    </div>
                    <div v-if="detailLoading" class="lv-detail-loading">
                      <div v-for="n in 3" :key="n" class="id-skel" style="height:42px;margin-bottom:6px"></div>
                    </div>
                    <div v-else class="lv-detail-list id-fade-in" :key="detailType + '-' + detailRequests.length">
                      <div v-for="r in detailFiltered" :key="r.requestid" class="lv-detail-row" :class="{ 'is-dim': r.status !== 'approved' }">
                        <div style="flex:1;min-width:0">
                          <div style="font-size:13px;font-weight:700">{{ leaveTypeLabel(r.leavetype) }}
                            <span class="badge" :class="leaveStatusClass(r.status)" style="font-size:10px">{{ leaveStatusLabel(r.status) }}</span></div>
                          <div style="font-size:11.5px;color:var(--c-text-muted);overflow-wrap:anywhere">
                            {{ leaveDateLabel(r) }} ({{ leaveDays(r) }} {{ $t('grouping.daysUnit') }})<template v-if="crossYearNote(r)"> {{ crossYearNote(r) }}</template><template v-if="r.dayportion && r.dayportion !== 'full'"> · {{ dayPortionLabel(r.dayportion) }}</template><template v-if="r.reason"> • {{ r.reason }}</template><a v-if="r.attachment_url" :href="r.attachment_url" target="_blank" rel="noopener" class="lv-attach" @click.stop> <span class="mdi mdi-paperclip"></span>{{ $t('grouping.leaveAttachView') }}</a>
                          </div>
                          <div v-if="submittedAtLabel(r)" style="font-size:11px;color:var(--c-text-muted)"><span class="mdi mdi-clock-outline"></span> {{ $t('grouping.submittedAt') }} {{ submittedAtLabel(r) }}</div>
                          <div v-if="r.reviewnote" style="font-size:11px;color:var(--c-text-muted);overflow-wrap:anywhere">↳ {{ r.reviewnote }}<template v-if="r.reviewby"> ({{ r.reviewby }})</template></div>
                        </div>
                      </div>
                      <div v-if="detailFiltered.length === 0" class="lv-detail-empty">{{ $t('grouping.noLeaveReq') }}</div>
                      <div v-if="detailPendingCount > 0" class="lv-detail-footer">
                        <span>{{ $t('grouping.leavePendingCountNote', { n: detailPendingCount, type: detailType ? (' (' + leaveTypeLabel(detailType) + ')') : '' }) }}</span>
                        <button type="button" class="lv-detail-cta" @click="scrollToPendingCard">{{ $t('grouping.goToPendingCard') }} <span class="mdi mdi-arrow-up"></span></button>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
            <tr v-if="overviewStaff.length === 0"><td colspan="5" style="text-align:center;color:var(--c-text-muted);padding:14px">{{ $t('common.noData') }}</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ── โหมด "อนุมัติการลา" (ผู้จัดการ head — เมนู leaveapproval): ประวัติที่พิจารณาแล้ว (พับเก็บ) ──── -->
    <div v-if="approvalMode" class="lv-card">
      <div class="lv-cardhead lv-history-head" @click="historyCollapsed = !historyCollapsed">
        <span class="strong"><span class="mdi mdi-history"></span> {{ $t('grouping.leaveHistoryTitle') }}</span>
        <span class="lv-history-toggle">
          {{ $t('grouping.leaveHistoryCount', { n: historyRequests.length }) }}
          <span class="mdi" :class="historyCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up'"></span>
        </span>
      </div>
      <template v-if="!historyCollapsed">
        <p class="lv-hint">{{ $t('grouping.leaveHistorySub') }}</p>
        <div class="lv-historytools">
          <id-select v-model="statusFilter" :options="historyStatusFilterOptions" style="width:150px"></id-select>
          <div class="lv-viewtoggle">
            <button type="button" :class="{ active: historyViewMode === 'grouped' }" @click="historyViewMode = 'grouped'">{{ $t('grouping.viewGrouped') }}</button>
            <button type="button" :class="{ active: historyViewMode === 'chrono' }" @click="historyViewMode = 'chrono'">{{ $t('grouping.viewChrono') }}</button>
          </div>
        </div>

        <!-- มุมมองจัดกลุ่มตามประเภท (ค่าเริ่มต้น) -->
        <div v-if="historyViewMode === 'grouped'" class="lv-grouplist id-fade-in" :key="'histgrp' + statusFilter + leaveRequests.length">
          <div v-for="g in historyGroupedByType" :key="g.type" class="lv-group">
            <div class="lv-group-head" @click="toggleHistoryGroup(g.type)">
              <span><span class="mdi" :class="historyGroupOpen[g.type] ? 'mdi-chevron-down' : 'mdi-chevron-right'"></span> {{ leaveTypeLabel(g.type) }}</span>
              <span class="lv-group-count">{{ $t('grouping.leaveHistoryCount', { n: g.requests.length }) }}</span>
            </div>
            <div v-if="historyGroupOpen[g.type]" class="lv-group-body">
              <div v-for="r in g.requests" :key="r.requestid" class="lv-row">
                <span class="lv-dot" :style="{ background: r.color || '#8b5cf6' }"></span>
                <div style="flex:1;min-width:0">
                  <div style="font-size:13.5px;font-weight:700">{{ r.displayname }}
                    <span class="badge" :class="leaveStatusClass(r.status)" style="font-size:10.5px">{{ leaveStatusLabel(r.status) }}</span>
                  </div>
                  <div style="font-size:12px;color:var(--c-text-muted);overflow-wrap:anywhere">
                    {{ leaveDateLabel(r) }} ({{ leaveDays(r) }} {{ $t('grouping.daysUnit') }})<template v-if="r.dayportion && r.dayportion !== 'full'"> · {{ dayPortionLabel(r.dayportion) }}</template><template v-if="r.reason"> • {{ r.reason }}</template><a v-if="r.attachment_url" :href="r.attachment_url" target="_blank" rel="noopener" class="lv-attach" @click.stop> <span class="mdi mdi-paperclip"></span>{{ $t('grouping.leaveAttachView') }}</a>
                  </div>
                  <div v-if="quotaContext(r, false)" style="font-size:11.5px;color:var(--c-text-muted)">{{ quotaContext(r, false) }}</div>
                  <div v-if="submittedAtLabel(r)" style="font-size:11px;color:var(--c-text-muted)"><span class="mdi mdi-clock-outline"></span> {{ $t('grouping.submittedAt') }} {{ submittedAtLabel(r) }}</div>
                  <div v-if="r.reviewnote" style="font-size:11.5px;color:var(--c-text-muted)">↳ {{ r.reviewnote }} ({{ r.reviewby }})</div>
                </div>
                <button v-if="r.status === 'approved' || r.status === 'rejected'" class="id-btn id-btn-ghost" style="color:var(--c-error)" @click="askCancelReview(r)">
                  <span class="mdi mdi-undo-variant"></span> {{ $t('grouping.cancelReviewBtn') }}</button>
              </div>
            </div>
          </div>
          <div v-if="historyGroupedByType.length === 0" class="id-empty" style="padding:18px">
            <div class="mdi mdi-inbox-outline"></div>
            <div style="margin-top:8px">{{ $t('grouping.noLeaveReq') }}</div>
          </div>
        </div>

        <!-- มุมมองเรียงตามวันที่ลา (ทุกคนทุกประเภทปนกัน ล่าสุดก่อน) -->
        <div v-else class="lv-list id-fade-in" :key="'histchrono' + statusFilter + leaveRequests.length">
          <div v-for="r in historyChronological" :key="r.requestid" class="lv-row">
            <span class="lv-dot" :style="{ background: r.color || '#8b5cf6' }"></span>
            <div style="flex:1;min-width:0">
              <div style="font-size:13.5px;font-weight:700">{{ r.displayname }}
                <span class="badge badge-neutral" style="font-size:10.5px">{{ leaveTypeLabel(r.leavetype) }}</span>
                <span class="badge" :class="leaveStatusClass(r.status)" style="font-size:10.5px">{{ leaveStatusLabel(r.status) }}</span>
              </div>
              <div style="font-size:12px;color:var(--c-text-muted);overflow-wrap:anywhere">
                {{ leaveDateLabel(r) }} ({{ leaveDays(r) }} {{ $t('grouping.daysUnit') }})<template v-if="r.dayportion && r.dayportion !== 'full'"> · {{ dayPortionLabel(r.dayportion) }}</template><template v-if="r.reason"> • {{ r.reason }}</template><a v-if="r.attachment_url" :href="r.attachment_url" target="_blank" rel="noopener" class="lv-attach" @click.stop> <span class="mdi mdi-paperclip"></span>{{ $t('grouping.leaveAttachView') }}</a>
              </div>
              <div v-if="quotaContext(r)" style="font-size:11.5px;color:var(--c-text-muted)">{{ quotaContext(r) }}</div>
              <div v-if="submittedAtLabel(r)" style="font-size:11px;color:var(--c-text-muted)"><span class="mdi mdi-clock-outline"></span> {{ $t('grouping.submittedAt') }} {{ submittedAtLabel(r) }}</div>
              <div v-if="r.reviewnote" style="font-size:11.5px;color:var(--c-text-muted)">↳ {{ r.reviewnote }} ({{ r.reviewby }})</div>
            </div>
            <button v-if="r.status === 'approved' || r.status === 'rejected'" class="id-btn id-btn-ghost" style="color:var(--c-error)" @click="askCancelReview(r)">
              <span class="mdi mdi-undo-variant"></span> {{ $t('grouping.cancelReviewBtn') }}</button>
          </div>
          <div v-if="historyChronological.length === 0" class="id-empty" style="padding:18px">
            <div class="mdi mdi-inbox-outline"></div>
            <div style="margin-top:8px">{{ $t('grouping.noLeaveReq') }}</div>
          </div>
        </div>
      </template>
    </div>

    <!-- ยื่นใบลา -->
    <id-modal v-model="leaveSubmitDialog" size="md" icon="mdi-email-send-outline" :title="$t('grouping.myLeaveSubmit')" persistent>
      <div class="form-grid">
        <div class="field full"><label>{{ $t('grouping.leaveTypeLabel') }} <span class="req">*</span></label>
          <id-select v-model="leaveForm.leavetype" :options="leaveTypeOptions"></id-select></div>
        <div class="field"><label>{{ $t('grouping.leaveFrom') }} <span class="req">*</span></label>
          <id-date v-model="leaveForm.startdate" :placeholder="$t('common.selectDate')" @update:model-value="onLeaveDateChange"></id-date></div>
        <div class="field"><label>{{ $t('grouping.leaveTo') }} <span class="req">*</span></label>
          <id-date v-model="leaveForm.enddate" :placeholder="$t('common.selectDate')" @update:model-value="onLeaveDateChange"></id-date></div>
        <div class="field full"><label>{{ $t('grouping.dayPortionLabel') }}</label>
          <id-select v-model="leaveForm.dayportion" :options="dayPortionOptions" :disabled="!leaveIsSingleDay"></id-select>
          <small v-if="!leaveIsSingleDay" style="display:block;margin-top:2px;color:var(--c-text-muted)">{{ $t('grouping.leaveMultiDayHint') }}</small></div>
        <div class="field full"><label>{{ $t('grouping.leaveReason') }}</label>
          <input class="id-input" v-model="leaveForm.reason" :placeholder="$t('grouping.leaveReasonPh')"></div>
        <div class="field full"><label>{{ $t('grouping.leaveAttach') }}</label>
          <label class="id-upload">
            <span class="mdi mdi-paperclip"></span>
            <span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ leaveFile ? leaveFile.name : $t('grouping.leaveAttachPick') }}</span>
            <input ref="leaveFileInput" type="file" accept="image/jpeg,image/png,image/gif,application/pdf" hidden @change="onLeaveFile">
          </label>
          <small style="display:block;margin-top:2px;color:var(--c-text-muted)">{{ $t('grouping.leaveAttachHint') }}
            <a v-if="leaveFile" href="#" style="color:var(--c-error)" @click.prevent="clearLeaveFile">{{ $t('grouping.leaveAttachRemove') }}</a></small></div>
      </div>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="leaveSubmitDialog = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" :disabled="!leaveFormValid" @click="submitLeave">
          <span class="mdi mdi-send"></span> {{ $t('grouping.leaveSubmitBtn') }}</button>
      </template>
    </id-modal>

    <!-- ยืนยันอนุมัติ/ปฏิเสธ -->
    <id-modal v-model="reviewDialog" size="sm" :icon="reviewApprove ? 'mdi-check-circle' : 'mdi-close-circle'"
      :title="reviewApprove ? $t('grouping.approve') : $t('grouping.reject')" persistent>
      <p style="margin:0 0 10px">{{ reviewApprove
        ? $t('grouping.confirmApprove', { name: reviewTarget ? reviewTarget.displayname : '' })
        : $t('grouping.confirmReject', { name: reviewTarget ? reviewTarget.displayname : '' }) }}</p>
      <div class="field full"><label>{{ $t('grouping.reviewNote') }}</label>
        <input class="id-input" v-model="reviewNote"></div>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="reviewDialog = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" :style="reviewApprove ? '' : 'background:var(--c-error)'" @click="confirmReview">
          <span class="mdi" :class="reviewApprove ? 'mdi-check' : 'mdi-close'"></span> {{ $t('btn.ok') }}</button>
      </template>
    </id-modal>

    <!-- audit D5: อนุมัติใบลาที่โค้ชมีคาบ Private รับจองไว้แล้วในช่วงลา — เตือนสองชั้นให้
         ผู้อนุมัติเห็นรายการคาบก่อน แล้วยืนยันเอง (ต้องไปย้าย/หาคนสอนแทนเองต่อ) -->
    <id-modal v-model="pvConfirmDialog" size="md" icon="mdi-calendar-alert" :title="$t('common.privateConflictTitle')" persistent>
      <p style="margin:0 0 10px">{{ pvConfirmMessage }}</p>
      <ul style="margin:0; padding-left:20px">
        <li v-for="(b, i) in pvConfirmList" :key="i">{{ $formatShortDate(b.bookingdate) }} · {{ b.classtime }}</li>
      </ul>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="pvConfirmDialog = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" style="background:var(--c-warning)" @click="pvConfirmDialog = false; confirmReview(true)">
          <span class="mdi mdi-check"></span> {{ $t('common.confirm') }}</button>
      </template>
    </id-modal>

    <!-- ยกเลิกใบลาที่พิจารณาไปแล้ว (เผื่ออนุมัติ/ปฏิเสธผิด) -->
    <id-modal v-model="cancelReviewDialog" size="sm" icon="mdi-undo-variant" :title="$t('grouping.cancelReviewTitle')" persistent>
      <p style="margin:0 0 10px">{{ $t('grouping.confirmCancelReview', { name: cancelReviewTarget ? cancelReviewTarget.displayname : '' }) }}</p>
      <div class="field full"><label>{{ $t('grouping.reviewNote') }}</label>
        <input class="id-input" v-model="reviewNote"></div>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="cancelReviewDialog = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" style="background:var(--c-error)" @click="confirmCancelReview">
          <span class="mdi mdi-undo-variant"></span> {{ $t('btn.ok') }}</button>
      </template>
    </id-modal>

    <!-- ยืนยันยกเลิกคำขอ -->
    <id-modal v-model="leaveCancelDialog" size="sm" icon="mdi-close-circle" :title="$t('grouping.cancelReq')" persistent>
      <p style="margin:0">{{ $t('grouping.confirmCancelReq') }}</p>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="leaveCancelDialog = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" style="background:var(--c-error)" @click="confirmCancelLeave">
          <span class="mdi mdi-close"></span> {{ $t('btn.ok') }}</button>
      </template>
    </id-modal>

    <!-- กำหนดโควต้า (head เท่านั้น) -->
    <id-modal v-model="quotaDialog" size="lg" icon="mdi-tune" :title="$t('grouping.quotaTitle')" :subtitle="$t('grouping.quotaSub')">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px">
        <button type="button" class="id-btn id-btn-ghost" style="padding:4px 10px" @click="changeQuotaYear(-1)"><span class="mdi mdi-chevron-left"></span></button>
        <span class="strong" style="font-size:14.5px">{{ $t('grouping.quotaYear') }} {{ displayYearOf(quotaYear) }}</span>
        <button type="button" class="id-btn id-btn-ghost" style="padding:4px 10px" @click="changeQuotaYear(1)"><span class="mdi mdi-chevron-right"></span></button>
      </div>
      <div class="lv-quota-grid">
        <div class="lv-qg-head">
          <span></span>
          <span>{{ leaveTypeLabel('sick') }}</span>
          <span>{{ leaveTypeLabel('personal') }}</span>
          <span>{{ leaveTypeLabel('vacation') }}</span>
          <span></span>
        </div>
        <div v-for="s in staff" :key="s.username" class="lv-qg-row">
          <span class="lv-qg-name">
            <span class="badge badge-neutral" style="font-size:10px">{{ roleLabel(s.usertype) }}</span>
            {{ staffName(s) }}</span>
          <span><input class="id-input lv-qg-num" type="number" min="0" max="365" v-model.number="s.form.sick_days">
            <small class="lv-qg-used">{{ $t('grouping.quotaUsedShort', { n: s.used.sick }) }}</small></span>
          <span><input class="id-input lv-qg-num" type="number" min="0" max="365" v-model.number="s.form.personal_days">
            <small class="lv-qg-used">{{ $t('grouping.quotaUsedShort', { n: s.used.personal }) }}</small></span>
          <span><input class="id-input lv-qg-num" type="number" min="0" max="365" v-model.number="s.form.vacation_days">
            <small class="lv-qg-used">{{ $t('grouping.quotaUsedShort', { n: s.used.vacation }) }}</small></span>
          <span><button class="id-btn id-btn-ghost" style="padding:5px 10px" :disabled="!quotaRowValid(s)" @click="saveQuota(s)">
            <span class="mdi mdi-content-save"></span></button></span>
        </div>
        <div v-if="staff.length === 0" class="id-empty" style="padding:18px">
          <div class="mdi mdi-account-question-outline"></div>
          <div style="margin-top:8px">{{ $t('common.noData') }}</div>
        </div>
      </div>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="quotaDialog = false">{{ $t('common.close') }}</button>
        <button class="id-btn id-btn-primary" :disabled="staff.length === 0 || !allQuotasValid" @click="saveAllQuotas">
          <span class="mdi mdi-content-save-all"></span> {{ $t('grouping.saveAllQuota') }}</button>
      </template>
    </id-modal>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import { mapGetters } from 'vuex'
import { displayYear } from '@/utils/locale'

export default {
  name: 'CoachLeave',
  props: {
    // โหมดหน้า: false = "ยื่นใบลา" (พนักงานทุกคน), true = "อนุมัติการลา" (ผู้จัดการ head — เมนู leaveapproval)
    // หน้าเดียว 2 state ผ่าน App.vue เหมือน GroupingSheet; backend เช็คสิทธิ์ซ้ำทุก endpoint
    approvalMode: { type: Boolean, default: false },
    // ผู้จัดการ (head) เท่านั้น: ปุ่มกำหนดโควต้า
    quotaEditable: { type: Boolean, default: false },
  },
  emits: ['onErrorHandler', 'onInfoHandler', 'onLoading', 'onClickChangeState'],
  data: () => ({
    myLoaded: false,
    myLeaves: [],
    myYear: null,
    myQuota: null,
    myUsed: { sick: 0, personal: 0, vacation: 0, unpaid: 0 },
    myPending: { sick: 0, personal: 0, vacation: 0, unpaid: 0 },
    // มุมมองประวัติของฉัน: จัดกลุ่มตามประเภท (ค่าเริ่มต้น) หรือเรียงตามวันที่ลา
    myViewMode: 'grouped',
    myGroupOpen: { sick: true, personal: true, vacation: true, unpaid: true },

    leaveRequests: [],
    pendingLeaveCount: 0,
    quotaByUser: {},
    reqYear: null,
    statusFilter: '',

    leaveSubmitDialog: false,
    leaveForm: { leavetype: 'sick', startdate: null, enddate: null, dayportion: 'full', reason: '' },
    leaveFile: null,

    reviewDialog: false,
    pvConfirmDialog: false, // audit D5: ยืนยันอนุมัติลาทับคาบ Private
    pvConfirmMessage: '',
    pvConfirmList: [],
    reviewTarget: null,
    reviewApprove: true,
    reviewNote: '',

    leaveCancelDialog: false,
    leaveCancelTarget: null,

    // ยกเลิกใบลาที่พิจารณาไปแล้ว (ฝั่งผู้จัดการ — เผื่ออนุมัติ/ปฏิเสธผิด)
    cancelReviewDialog: false,
    cancelReviewTarget: null,

    quotaDialog: false,
    quotaYear: Number(moment().format('YYYY')),
    staff: [],

    // ตารางภาพรวมการลารายปี (หน้าอนุมัติ)
    overviewYear: Number(moment().format('YYYY')),
    overviewStaff: [],
    // กางดูใบลารายคนจากตารางภาพรวม (คลิกชื่อแถว) — เก็บ username ที่เปิดอยู่ + รายการใบลาของคนนั้น
    expandedUser: null,
    detailRequests: [],
    detailLoading: false,
    detailType: '', // filter ประเภทลาใน panel รายละเอียด ('' = ทุกประเภท)

    // การ์ด "ประวัติที่พิจารณาแล้ว" พับเก็บโดย default — ไม่บังงานเร่งด่วนของการ์ด "รอดำเนินการ" ด้านบน
    historyCollapsed: true,
    // มุมมองประวัติ: จัดกลุ่มตามประเภท (ค่าเริ่มต้น, พับทุกกลุ่มไว้ก่อนเพราะข้อมูลรวมทุกคนอาจเยอะ) หรือเรียงตามวันที่ลา
    historyViewMode: 'grouped',
    historyGroupOpen: { sick: false, personal: false, vacation: false, unpaid: false },

    interval: null,
  }),
  computed: {
    ...mapGetters({ token: 'getToken' }),
    leaveTypeOptions() {
      // unpaid = ลาทั่วไป (ไม่ได้รับเงินเดือน) — ไม่ตัดโควต้า จึงไม่มีการ์ดโควต้า แต่ยื่นได้
      return ['sick', 'personal', 'vacation', 'unpaid'].map((t) => ({ value: t, label: this.leaveTypeLabel(t) }))
    },
    // ตัวกรองสถานะในการ์ด "ประวัติที่พิจารณาแล้ว" — ไม่มี "รออนุมัติ" เพราะย้ายไปอยู่การ์ด "รอดำเนินการ" แยกต่างหากแล้ว
    historyStatusFilterOptions() {
      return [
        { value: '', label: this.$t('grouping.statusAll') },
        ...['approved', 'rejected', 'cancelled'].map((s) => ({ value: s, label: this.leaveStatusLabel(s) })),
      ]
    },
    pendingRequests() {
      return this.leaveRequests.filter((r) => r.status === 'pending')
    },
    historyRequests() {
      return this.leaveRequests.filter((r) => r.status !== 'pending')
    },
    filteredHistoryRequests() {
      if (!this.statusFilter) return this.historyRequests
      return this.historyRequests.filter((r) => r.status === this.statusFilter)
    },
    // จัดกลุ่ม "ประวัติที่พิจารณาแล้ว" ตามประเภทลา (เฉพาะประเภทที่มีรายการ) — เรียงลำดับประเภทคงที่
    historyGroupedByType() {
      return ['sick', 'personal', 'vacation', 'unpaid']
        .map((type) => ({ type, requests: this.filteredHistoryRequests.filter((r) => r.leavetype === type) }))
        .filter((g) => g.requests.length > 0)
    },
    // มุมมองเรียงตามวันที่ลา (startdate) ล่าสุดก่อน — ต่างจากลำดับที่ backend ส่งมา (createdate)
    historyChronological() {
      return [...this.filteredHistoryRequests].sort((a, b) => (a.startdate < b.startdate ? 1 : a.startdate > b.startdate ? -1 : 0))
    },
    // ตัวเลือกกรองประเภทลาใน panel รายละเอียดรายคน (ทุกประเภท + 4 ประเภท)
    detailTypeOptions() {
      return [
        { value: '', label: this.$t('grouping.leaveTypeAll') },
        ...['sick', 'personal', 'vacation', 'unpaid'].map((t) => ({ value: t, label: this.leaveTypeLabel(t) })),
      ]
    },
    detailFiltered() {
      if (!this.detailType) return this.detailRequests
      return this.detailRequests.filter((r) => r.leavetype === this.detailType)
    },
    // จำนวนใบ "รออนุมัติ" ในมุมมองที่กรองอยู่ตอนนี้ (ไม่ใช่รวมทุกประเภท) — ให้ footer CTA ตรงกับสิ่งที่เห็นจริง
    detailPendingCount() {
      return this.detailFiltered.filter((r) => r.status === 'pending').length
    },
    // จัดกลุ่มใบลาของฉันตามประเภท (เฉพาะประเภทที่มีรายการ) — เรียงลำดับประเภทคงที่
    myGroupedByType() {
      return ['sick', 'personal', 'vacation', 'unpaid']
        .map((type) => ({ type, requests: this.myLeaves.filter((r) => r.leavetype === type) }))
        .filter((g) => g.requests.length > 0)
    },
    // มุมมองเรียงตามวันที่ลา (startdate) ล่าสุดก่อน
    myChronological() {
      return [...this.myLeaves].sort((a, b) => (a.startdate < b.startdate ? 1 : a.startdate > b.startdate ? -1 : 0))
    },
    leaveIsSingleDay() {
      return !!this.leaveForm.startdate && this.leaveForm.startdate === this.leaveForm.enddate
    },
    leaveFormValid() {
      const f = this.leaveForm
      if (!f.leavetype || !f.startdate || !f.enddate || f.enddate < f.startdate) return false
      return true
    },
    // เต็มวัน / ครึ่งเช้า / ครึ่งบ่าย (ชุดเดียวกับหน้าจัดกลุ่มโค้ช)
    dayPortionOptions() {
      return ['full', 'morning', 'afternoon'].map((p) => ({ value: p, label: this.$t('grouping.dayPortion_' + p) }))
    },
    allQuotasValid() {
      return this.staff.every((s) => this.quotaRowValid(s))
    },
  },
  async created() {
    // โหมด "อนุมัติการลา" โหลดคำขอ + ตารางภาพรวม; โหมด "ยื่นใบลา" โหลดของตัวเอง
    if (this.approvalMode) { await this.loadRequests(); await this.loadOverview() }
    else await this.loadMy()
  },
  mounted() {
    // โพลเงียบทุก 60 วิ — ใบลาใหม่/ผลอนุมัติโผล่เองโดยไม่ต้องรีเฟรชหน้า (แพตเทิร์นเดียวกับ PrivateBookingManagement)
    this.interval = setInterval(() => {
      if (this.approvalMode) {
        this.loadRequests(true); this.loadOverview(true)
        if (this.expandedUser) this.loadStaffDetail(this.expandedUser, true)
      } else this.loadMy(true)
    }, 60000)
  },
  unmounted() { clearInterval(this.interval) },
  methods: {
    authHeaders() { return { headers: { Authorization: `Bearer ${this.token}` } } },
    // silent=true จากโพลเบื้องหลัง — กลืน error ชั่วคราวไว้ ไม่เด้ง dialog (ยกเว้น 401 ยังบังคับ login ใหม่)
    handleApiError(error, silent = false) {
      const serverMsg = error.response && error.response.data && error.response.data.message
      if (error.response && error.response.status === 401) {
        this.$emit('onErrorHandler', serverMsg || this.$t('auth.pleaseLoginAgain'))
        this.$emit('onClickChangeState', 'login')
      } else if (!silent) {
        this.$emit('onErrorHandler', serverMsg || error.message)
      }
    },
    displayYearOf(y) { return displayYear(y) },
    quotaOf(q, t) { return { sick: q.sick_days, personal: q.personal_days, vacation: q.vacation_days }[t] },
    // โควต้าคงเหลือ = เพดาน − อนุมัติแล้ว − รออนุมัติ (จำนวนที่ยื่นได้อีก) — ไม่ให้ติดลบ
    quotaRemain(t) {
      if (!this.myQuota) return 0
      return Math.max(0, this.quotaOf(this.myQuota, t) - (this.myUsed[t] || 0) - (this.myPending[t] || 0))
    },
    quotaRemainPct(t) {
      const total = this.myQuota ? this.quotaOf(this.myQuota, t) : 0
      return total > 0 ? Math.round((this.quotaRemain(t) / total) * 100) : 0
    },
    // สีแถบ/ตัวเลขคงเหลือ: แดงเมื่อ ≤2 วัน, เหลืองเมื่อ ≤4 วัน, ที่เหลือปกติ (เหมือนมิเตอร์หน้า Home)
    quotaRemainClass(t) {
      const r = this.quotaRemain(t)
      return r <= 2 ? 'is-low' : r <= 4 ? 'is-mid' : ''
    },
    leaveTypeLabel(t) {
      const key = `grouping.leaveType_${t}`
      const label = this.$t(key)
      return label === key ? t : label
    },
    leaveStatusLabel(s) {
      const key = `grouping.leaveStatus_${s}`
      const label = this.$t(key)
      return label === key ? s : label
    },
    leaveStatusClass(s) {
      return { pending: 'badge-warning', approved: 'badge-success', rejected: 'badge-error', cancelled: 'badge-neutral' }[s] || 'badge-neutral'
    },
    leaveDateLabel(r) {
      if (r.startdate === r.enddate) return this.$formatShortDate(r.startdate)
      return `${this.$formatShortDate(r.startdate)} – ${this.$formatShortDate(r.enddate)}`
    },
    leaveDays(r) {
      // ครึ่งวัน (วันเดียว) = 0.5, ไม่งั้นจำนวนวันปฏิทิน
      if (r.startdate === r.enddate && (r.dayportion === 'morning' || r.dayportion === 'afternoon')) return 0.5
      return moment(r.enddate, 'YYYY-MM-DD').diff(moment(r.startdate, 'YYYY-MM-DD'), 'days') + 1
    },
    dayPortionLabel(p) { return this.$t('grouping.dayPortion_' + (p || 'full')) },
    // วันเวลาที่พนักงานส่งคำขอ (createdate 'YYYY-MM-DD HH:mm' จาก backend) → วันที่ไทยแบบสั้น + เวลา
    submittedAtLabel(r) {
      if (!r.createdate) return ''
      const m = moment(r.createdate, 'YYYY-MM-DD HH:mm')
      if (!m.isValid()) return ''
      return `${this.$formatShortDate(m.format('YYYY-MM-DD'))} ${m.format('HH:mm')}`
    },
    roleLabel(usertype) { return this.$t({ 0: 'role.head', 1: 'role.admin', 2: 'role.coach' }[usertype] || 'role.head') },
    staffName(s) { return `${s.firstname || ''} ${s.lastname || ''}`.trim() || s.username },
    // บริบทโควต้าของแถวคำขอ (ฝั่งผู้จัดการ): แสดง "ยอดสะสมถึงใบนี้" (running total) เฉพาะใบที่อนุมัติแล้ว
    // เช่น "ลาพักร้อน: 1.5/6 วัน" — ไล่นับตามลำดับวันลา; ใบรออนุมัติ/ปฏิเสธ/ยกเลิก (cumulative=null) ไม่แสดง
    // withType=false ตัดคำนำหน้าประเภทออก (ใช้ในมุมมองจัดกลุ่มตามประเภทที่ประเภทถูกบอกไว้ที่หัวกลุ่มแล้ว)
    quotaContext(r, withType = true) {
      if (r.cumulative == null) return ''
      const info = this.quotaByUser[r.username]
      if (!info) return ''
      const prefix = withType ? `${this.leaveTypeLabel(r.leavetype)}: ` : ''
      if (!info.quota) return `${prefix}${this.$t('grouping.quotaUsedShort', { n: r.cumulative })} • ${this.$t('grouping.quotaNotSet')}`
      return `${prefix}${r.cumulative}/${this.quotaOf(info.quota, r.leavetype)} ${this.$t('grouping.daysUnit')}`
    },
    // บริบทโควต้าของคำขอที่ยัง "รออนุมัติ" (การ์ดรอดำเนินการ) — โชว์ยอดที่อนุมัติแล้วก่อนหน้า (ไม่รวมใบนี้เอง
    // เพราะยังไม่อนุมัติ) ต่างจาก quotaContext ที่โชว์ยอดสะสม "หลัง" อนุมัติใบนั้นแล้ว
    pendingQuotaContext(r) {
      if (r.leavetype === 'unpaid') return '' // ลาไม่รับเงินเดือนไม่มีโควต้า
      const info = this.quotaByUser[r.username]
      if (!info) return ''
      if (!info.quota) return `${this.leaveTypeLabel(r.leavetype)}: ${this.$t('grouping.quotaNotSet')}`
      const used = (info.used && info.used[r.leavetype]) || 0
      return `${this.leaveTypeLabel(r.leavetype)}: ${this.$t('grouping.quotaUsedShort', { n: used })} / ${this.quotaOf(info.quota, r.leavetype)} ${this.$t('grouping.daysUnit')}`
    },
    // หมายเหตุ "(N วันในปีนี้)" สำหรับใบลาข้ามปี (เช่น 30 ธ.ค.-2 ม.ค.) ใน panel รายละเอียดของตารางภาพรวม —
    // backend แนบ daysInYear มาให้ (เฉพาะ /getStaffLeaveDetail) ว่างเปล่าถ้าไม่ใช่ใบข้ามปี
    crossYearNote(r) {
      if (r.daysInYear == null) return ''
      const full = this.leaveDays(r)
      if (r.daysInYear >= full) return ''
      return this.$t('grouping.daysInYearNote', { n: r.daysInYear })
    },

    // ── ของฉัน ────────────────────────────────────────────────
    async loadMy(silent = false) {
      try {
        const res = await axios.post(this.baseURL + '/getMyLeaveRequests', {}, this.authHeaders())
        if (res.data.success) {
          this.myLeaves = res.data.requests || []
          this.myYear = res.data.year || null
          this.myQuota = res.data.quota || null
          this.myUsed = res.data.used || { sick: 0, personal: 0, vacation: 0 }
          this.myPending = res.data.pendingUsed || { sick: 0, personal: 0, vacation: 0 }
        }
      } catch (error) { this.handleApiError(error, silent) }
      this.myLoaded = true
    },
    openLeaveSubmit() {
      const start = moment().format('YYYY-MM-DD')
      this.leaveForm = { leavetype: 'sick', startdate: start, enddate: start, dayportion: 'full', reason: '' }
      this.clearLeaveFile()
      this.leaveSubmitDialog = true
    },
    onLeaveFile(e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      if (!['image/jpeg', 'image/png', 'image/gif', 'application/pdf'].includes(file.type)) {
        this.$emit('onErrorHandler', this.$t('grouping.leaveAttachBadType')); this.clearLeaveFile(); return
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$emit('onErrorHandler', this.$t('grouping.leaveAttachTooBig')); this.clearLeaveFile(); return
      }
      this.leaveFile = file
    },
    clearLeaveFile() {
      this.leaveFile = null
      if (this.$refs.leaveFileInput) this.$refs.leaveFileInput.value = ''
    },
    onLeaveDateChange() {
      const f = this.leaveForm
      if (f.enddate < f.startdate) f.enddate = f.startdate
      // ครึ่งวันได้เฉพาะวันเดียว — หลายวันบังคับเป็นเต็มวัน
      if (!this.leaveIsSingleDay) f.dayportion = 'full'
    },
    async submitLeave() {
      this.$emit('onLoading', true)
      try {
        const f = this.leaveForm
        const fd = new FormData()
        fd.append('leavetype', f.leavetype)
        fd.append('startdate', f.startdate)
        fd.append('enddate', f.enddate)
        fd.append('dayportion', this.leaveIsSingleDay ? f.dayportion : 'full')
        fd.append('reason', f.reason || '')
        if (this.leaveFile) fd.append('attachment', this.leaveFile)
        const res = await axios.post(this.baseURL + '/submitLeaveRequest', fd, this.authHeaders())
        if (res.data.success) {
          this.leaveSubmitDialog = false
          this.$emit('onInfoHandler', res.data.message)
          await this.loadMy()
          if (this.approvalMode) await this.loadRequests()
        } else {
          this.$emit('onErrorHandler', res.data.message)
        }
      } catch (error) { this.handleApiError(error) }
      this.$emit('onLoading', false)
    },
    askCancelLeave(r) {
      this.leaveCancelTarget = r
      this.leaveCancelDialog = true
    },
    toggleMyGroup(type) { this.myGroupOpen[type] = !this.myGroupOpen[type] },
    async confirmCancelLeave() {
      this.leaveCancelDialog = false
      this.$emit('onLoading', true)
      try {
        const res = await axios.post(this.baseURL + '/cancelLeaveRequest', { requestid: this.leaveCancelTarget.requestid }, this.authHeaders())
        if (res.data.success) {
          await this.loadMy()
          if (this.approvalMode) await this.loadRequests()
        } else {
          this.$emit('onErrorHandler', res.data.message)
        }
      } catch (error) { this.handleApiError(error) }
      this.$emit('onLoading', false)
    },

    // ── ผู้จัดการ/แอดมิน ──────────────────────────────────────
    async loadRequests(silent = false) {
      try {
        const res = await axios.post(this.baseURL + '/getLeaveRequests', {}, this.authHeaders())
        if (res.data.success) {
          this.leaveRequests = res.data.requests
          this.pendingLeaveCount = res.data.pendingCount
          this.quotaByUser = res.data.quotaByUser || {}
          this.reqYear = res.data.year || null
        }
      } catch (error) { this.handleApiError(error, silent) }
    },
    // ── ตารางภาพรวมการลารายปี (หน้าอนุมัติ) ───────────────────
    async loadOverview(silent = false) {
      const year = this.overviewYear
      try {
        const res = await axios.post(this.baseURL + '/getLeaveQuotas', { year }, this.authHeaders())
        // ผู้ใช้เพิ่งกดเปลี่ยนปีระหว่างรอ response — ทิ้งข้อมูลปีเก่า ไม่ให้ทับของใหม่
        if (year !== this.overviewYear) return
        if (res.data.success) this.overviewStaff = res.data.staff || []
      } catch (error) { this.handleApiError(error, silent) }
    },
    async changeOverviewYear(delta) {
      this.overviewYear += delta
      this.expandedUser = null // ปิด panel รายคนเมื่อเปลี่ยนปี (ข้อมูลคนละปี)
      await this.loadOverview()
    },
    toggleHistoryGroup(type) { this.historyGroupOpen[type] = !this.historyGroupOpen[type] },
    // กาง/หุบรายละเอียดใบลารายคน (คลิกชื่อในตารางภาพรวม)
    async toggleStaffDetail(s) {
      if (this.expandedUser === s.username) { this.expandedUser = null; return }
      this.expandedUser = s.username
      this.detailType = ''
      this.detailRequests = []
      await this.loadStaffDetail(s.username)
    },
    async loadStaffDetail(username, silent = false) {
      if (!silent) this.detailLoading = true
      try {
        const res = await axios.post(this.baseURL + '/getStaffLeaveDetail', { username, year: this.overviewYear }, this.authHeaders())
        // ผู้ใช้หุบ/เปลี่ยนคนระหว่างรอ response — ทิ้งผลเก่า ไม่ให้ทับ panel ปัจจุบัน
        if (this.expandedUser !== username) return
        if (res.data.success) this.detailRequests = res.data.requests || []
      } catch (error) { this.handleApiError(error, silent) }
      this.detailLoading = false
    },
    // footer ของ panel รายละเอียด — เลื่อนขึ้นไปที่การ์ด "รอดำเนินการ" ด้านบนสุดของหน้า
    scrollToPendingCard() {
      const el = this.$refs.pendingCard
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    ovQuota(s, t) { const v = s[`${t}_days`]; return v != null ? v : '—' },
    ovUsed(s, t) { return (s.used && s.used[t]) || 0 },
    ovPending(s, t) { return (s.pending && s.pending[t]) || 0 },
    ovRemain(s, t) {
      const q = s[`${t}_days`]
      if (q == null) return '—' // ยังไม่กำหนดโควต้า
      return Math.max(0, q - this.ovUsed(s, t) - this.ovPending(s, t))
    },
    ovRemainClass(s, t) {
      const q = s[`${t}_days`]
      if (q != null && this.ovUsed(s, t) > q) return 'is-over' // อนุมัติแล้วเกินเพดาน (หัวหน้าลดโควต้าย้อนหลัง)
      const r = this.ovRemain(s, t)
      if (r === '—') return ''
      return r <= 2 ? 'is-low' : r <= 4 ? 'is-mid' : ''
    },
    // Sub-label ใต้ตัวเลขคงเหลือ — 4 สถานะ: อนุมัติแล้ว(+รออนุมัติ) / ยังไม่กำหนดโควต้า / เกินโควต้า
    // 'unpaid' ไม่มีเพดาน ใช้แค่ 2 สถานะแรก (อนุมัติแล้ว / อนุมัติแล้ว+รออนุมัติ)
    ovSubLabel(s, t) {
      const used = this.ovUsed(s, t)
      const pending = this.ovPending(s, t)
      const approvedPart = () => {
        let label = this.$t('grouping.ovApprovedLabel', { n: used })
        if (pending > 0) label += ' · ' + this.$t('grouping.ovPendingNote', { n: pending })
        return label
      }
      if (t === 'unpaid') return approvedPart()
      const quota = s[`${t}_days`]
      if (quota == null) {
        return used > 0 ? this.$t('grouping.quotaNotSetWithUsed', { n: used }) : this.$t('grouping.quotaNotSet')
      }
      if (used > quota) {
        return this.$t('grouping.quotaOverLabel', { over: used - quota, used, quota })
      }
      return approvedPart()
    },
    askReview(r, approve) {
      this.reviewTarget = r
      this.reviewApprove = approve
      this.reviewNote = ''
      this.reviewDialog = true
    },
    async confirmReview(confirmPrivateBookings = false) {
      this.reviewDialog = false
      this.$emit('onLoading', true)
      const payload = {
        requestid: this.reviewTarget.requestid, approve: this.reviewApprove, reviewnote: this.reviewNote,
      }
      // audit D5: เช็ค === true เพราะปุ่มในไดอะล็อกหลักเรียก @click="confirmReview" ซึ่งส่ง MouseEvent (truthy)
      if (confirmPrivateBookings === true) payload.confirmPrivateBookings = true
      try {
        const res = await axios.post(this.baseURL + '/reviewLeaveRequest', payload, this.authHeaders())
        if (res.data.success) {
          this.$emit('onInfoHandler', res.data.message)
          await this.loadRequests()
          await this.loadOverview() // อนุมัติ/ปฏิเสธแล้ว ยอดในตารางภาพรวมเปลี่ยน
          await this.loadMy() // เผื่อ head/admin เป็นโค้ชเองด้วย — โควต้าตัวเองเปลี่ยน
          if (this.expandedUser) await this.loadStaffDetail(this.expandedUser, true) // อัปเดต panel ที่เปิดอยู่
        } else if (res.data.requireConfirm && res.data.confirmKind === 'privateBookings') {
          this.pvConfirmMessage = res.data.message || ''
          this.pvConfirmList = res.data.privateBookings || []
          this.pvConfirmDialog = true
        } else {
          this.$emit('onErrorHandler', res.data.message)
        }
      } catch (error) { this.handleApiError(error) }
      this.$emit('onLoading', false)
    },
    askCancelReview(r) {
      this.cancelReviewTarget = r
      this.reviewNote = ''
      this.cancelReviewDialog = true
    },
    async confirmCancelReview() {
      this.cancelReviewDialog = false
      this.$emit('onLoading', true)
      try {
        const res = await axios.post(this.baseURL + '/cancelLeaveReview', {
          requestid: this.cancelReviewTarget.requestid, reviewnote: this.reviewNote,
        }, this.authHeaders())
        if (res.data.success) {
          this.$emit('onInfoHandler', res.data.message)
          await this.loadRequests()
          await this.loadOverview() // ยกเลิกแล้ว โควต้า/ยอดในตารางภาพรวมเปลี่ยน
          await this.loadMy() // เผื่อ head/admin เป็นโค้ชเองด้วย — โควต้าตัวเองเปลี่ยน
          if (this.expandedUser) await this.loadStaffDetail(this.expandedUser, true) // อัปเดต panel ที่เปิดอยู่
        } else {
          this.$emit('onErrorHandler', res.data.message)
        }
      } catch (error) { this.handleApiError(error) }
      this.$emit('onLoading', false)
    },

    // ── โควต้า (head) ─────────────────────────────────────────
    // โหลดโควต้าให้ครบก่อนเปิด — เดิมเปิดกล่องเปล่าแล้วรายชื่อค่อยไหลเข้ามา
    async openQuotaDialog() {
      this.$emit('onLoading', true)
      try {
        await this.loadQuotas()
      } finally {
        this.$emit('onLoading', false)
      }
      this.quotaDialog = true
    },
    async changeQuotaYear(delta) {
      this.quotaYear += delta
      await this.loadQuotas()
    },
    async loadQuotas() {
      try {
        const res = await axios.post(this.baseURL + '/getLeaveQuotas', { year: this.quotaYear }, this.authHeaders())
        if (res.data.success) {
          this.staff = res.data.staff.map((s) => ({
            ...s,
            form: {
              sick_days: s.sick_days != null ? s.sick_days : 30,
              personal_days: s.personal_days != null ? s.personal_days : 3,
              vacation_days: s.vacation_days != null ? s.vacation_days : 6,
            },
          }))
        }
      } catch (error) { this.handleApiError(error) }
    },
    quotaRowValid(s) {
      return ['sick_days', 'personal_days', 'vacation_days'].every((k) => Number.isInteger(s.form[k]) && s.form[k] >= 0 && s.form[k] <= 365)
    },
    async saveQuota(s) {
      this.$emit('onLoading', true)
      try {
        const res = await axios.post(this.baseURL + '/saveLeaveQuota', {
          username: s.username, year: this.quotaYear, ...s.form,
        }, this.authHeaders())
        if (res.data.success) {
          this.$emit('onInfoHandler', res.data.message)
          await this.loadQuotas()
          await this.loadMy()
          if (this.approvalMode) await this.loadRequests()
        } else {
          this.$emit('onErrorHandler', res.data.message)
        }
      } catch (error) { this.handleApiError(error) }
      this.$emit('onLoading', false)
    },
    async saveAllQuotas() {
      if (!this.allQuotasValid || this.staff.length === 0) return
      this.$emit('onLoading', true)
      try {
        const quotas = this.staff.map((s) => ({ username: s.username, ...s.form }))
        const res = await axios.post(this.baseURL + '/saveLeaveQuotas', {
          year: this.quotaYear, quotas,
        }, this.authHeaders())
        if (res.data.success) {
          this.$emit('onInfoHandler', res.data.message)
          await this.loadQuotas()
          await this.loadMy()
          if (this.approvalMode) await this.loadRequests()
        } else {
          this.$emit('onErrorHandler', res.data.message)
        }
      } catch (error) { this.handleApiError(error) }
      this.$emit('onLoading', false)
    },
  },
}
</script>

<style scoped>
.lv-card { background: var(--c-surface); border: 1px solid var(--c-border); border-radius: 14px; padding: 14px 16px; margin-bottom: 16px; }
.lv-cardhead { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; margin-bottom: 10px; }
.lv-cardhead .strong { font-size: 15px; display: inline-flex; align-items: center; gap: 7px; }
.lv-year { font-weight: 400; font-size: 12.5px; color: var(--c-text-muted); }
.lv-hint { font-size: 12px; color: var(--c-text-muted); margin: 0 0 8px; }
.lv-quota-row { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin-bottom: 12px; }
.lv-quota-row-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
@media (max-width: 700px) { .lv-quota-row { grid-template-columns: 1fr; } }
@media (min-width: 701px) and (max-width: 900px) { .lv-quota-row-4 { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
.lv-quota-card { border: 1px solid var(--c-border); border-radius: 10px; padding: 10px 12px; background: var(--c-surface-2); }
.lv-quota-type { font-size: 12.5px; font-weight: 700; color: var(--c-text-muted); }
.lv-quota-remainlabel { font-size: 10.5px; font-weight: 600; color: var(--c-text-muted); text-transform: uppercase; letter-spacing: .02em; margin-top: 2px; }
.lv-quota-num { font-size: 24px; font-weight: 800; color: var(--c-primary); line-height: 1.1; }
.lv-quota-num.is-mid { color: var(--c-warning); }
.lv-quota-num.is-low { color: var(--c-error); }
.lv-quota-total { font-size: 13px; font-weight: 400; color: var(--c-text-muted); margin-left: 4px; }
/* แถบแสดงสัดส่วนคงเหลือ (ยิ่งใช้มากยิ่งสั้น) */
.lv-quota-bar { height: 6px; border-radius: 999px; background: var(--c-border); overflow: hidden; margin: 6px 0 5px; }
.lv-quota-bar > span { display: block; height: 100%; border-radius: 999px; background: var(--c-primary); transition: width .3s ease; }
.lv-quota-bar > span.is-mid { background: var(--c-warning); }
.lv-quota-bar > span.is-low { background: var(--c-error); }
.lv-quota-sub { font-size: 11.5px; color: var(--c-text-muted); }
.lv-list { display: flex; flex-direction: column; }
.lv-row { display: flex; align-items: center; gap: 10px; padding: 10px 4px; border-bottom: 1px solid var(--c-border); flex-wrap: wrap; }
.lv-attach { color: var(--c-primary); font-weight: 600; text-decoration: none; white-space: nowrap; }
.lv-attach:hover { text-decoration: underline; }
.lv-row:last-child { border-bottom: 0; }
.lv-dot { width: 14px; height: 14px; border-radius: 50%; flex: 0 0 14px; }
.lv-check { display: inline-flex; align-items: center; gap: 7px; font-size: 13px; cursor: pointer; user-select: none;
  border: 1px solid var(--c-border); border-radius: 999px; padding: 5px 12px 5px 7px; }
.lv-check.on { border-color: var(--c-primary); background: var(--c-surface-3); }
.lv-pick { display: flex; flex-wrap: wrap; gap: 8px; }
.lv-quota-grid { display: flex; flex-direction: column; gap: 4px; }
.lv-qg-head, .lv-qg-row { display: grid; grid-template-columns: minmax(110px, 1.2fr) 1fr 1fr 1fr 44px; gap: 8px; align-items: center; }
.lv-qg-head { font-size: 11.5px; font-weight: 700; color: var(--c-text-muted); padding: 0 2px; }
.lv-qg-row { padding: 6px 2px; border-top: 1px dashed var(--c-border); }
.lv-qg-name { font-size: 13px; font-weight: 700; display: inline-flex; align-items: center; gap: 7px; min-width: 0; overflow-wrap: anywhere; }
.lv-qg-num { width: 100%; text-align: center; }
.lv-qg-used { display: block; font-size: 10.5px; color: var(--c-text-muted); text-align: center; margin-top: 2px; }
/* ตารางภาพรวมการลารายปี */
.lv-ov-scroll { overflow-x: auto; }
.lv-ov { width: 100%; border-collapse: collapse; font-size: 12.5px; }
.lv-ov th, .lv-ov td { padding: 8px 10px; text-align: center; white-space: nowrap; border-bottom: 1px solid var(--c-border); }
.lv-ov th { font-weight: 700; color: var(--c-text-muted); font-size: 11.5px; background: var(--c-surface-2); }
.lv-ov th:first-child, .lv-ov td:first-child { text-align: left; }
.lv-ov-name { font-weight: 700; }
.lv-ov-remain { font-size: 16px; font-weight: 800; color: var(--c-primary); }
.lv-ov-remain.is-mid { color: var(--c-warning); }
.lv-ov-remain.is-low, .lv-ov-remain.is-over { color: var(--c-error); }
.lv-ov-of { font-size: 11px; color: var(--c-text-muted); margin-left: 2px; }
.lv-ov-used { display: block; font-size: 10px; color: var(--c-text-muted); }
/* แถวคลิกกางรายละเอียดใบลารายคน + panel รายละเอียด */
.lv-ov-row { cursor: pointer; transition: background .15s ease; }
.lv-ov-row:hover { background: var(--c-surface-2); }
.lv-ov-row.is-open { background: var(--c-surface-3); }
.lv-ov-caret { color: var(--c-text-muted); margin-right: 3px; }
.lv-ov-detailrow > td { padding: 0 !important; text-align: left; white-space: normal; background: var(--c-surface-2); }
.lv-detail { padding: 12px 14px; }
.lv-detail-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; margin-bottom: 8px; }
.lv-detail-title { font-size: 13px; font-weight: 700; display: inline-flex; align-items: center; gap: 6px; }
.lv-detail-list { display: flex; flex-direction: column; }
.lv-detail-row { display: flex; align-items: flex-start; gap: 10px; padding: 8px 2px; border-bottom: 1px solid var(--c-border); }
.lv-detail-row:last-child { border-bottom: 0; }
/* ใบที่ไม่ใช่ "อนุมัติแล้ว" (รออนุมัติ/ปฏิเสธ/ยกเลิก) จางลง — ไม่นับรวมในตัวเลข "คงเหลือ" ด้านบน
   ใช้ opacity เท่านั้น ไม่ใช้ font-style:italic (ฟอนต์ไทยไม่มี italic จริง จะเอียงปลอมจนสระ/วรรณยุกต์เยื้อง) */
.lv-detail-row.is-dim { opacity: .6; }
.lv-detail-empty { text-align: center; color: var(--c-text-muted); padding: 14px; font-size: 12.5px; }
.lv-detail-loading { padding: 2px 0; }
.lv-detail-footer { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap;
  margin-top: 8px; padding-top: 8px; border-top: 1px dashed var(--c-border); font-size: 11.5px; color: var(--c-text-muted); }
.lv-detail-cta { background: none; border: none; padding: 0; font: inherit; cursor: pointer;
  color: var(--c-primary); font-weight: 700; white-space: nowrap; text-decoration: none; }
.lv-detail-cta:hover { text-decoration: underline; }
/* หัวการ์ด "ประวัติที่พิจารณาแล้ว" — คลิกพับ/กางได้ */
.lv-history-head { cursor: pointer; }
.lv-history-toggle { display: inline-flex; align-items: center; gap: 4px; font-size: 12.5px; color: var(--c-text-muted); }
/* สลับมุมมอง จัดกลุ่มตามประเภท / เรียงตามวันที่ — ใช้ในการ์ด "ใบลาของฉัน" และ "ประวัติที่พิจารณาแล้ว" */
.lv-viewtoggle { display: inline-flex; background: var(--c-surface-2); border-radius: 10px; padding: 2px; gap: 2px; }
.lv-viewtoggle button { border: none; background: transparent; padding: 5px 12px; font-size: 12px; border-radius: 8px; cursor: pointer; color: var(--c-text-muted); font-family: inherit; }
.lv-viewtoggle button.active { background: var(--c-primary); color: var(--c-on-primary); }
.lv-historytools { display: flex; gap: 8px; align-items: center; justify-content: space-between; flex-wrap: wrap; margin-bottom: 10px; }
/* รายการจัดกลุ่มตามประเภทลา — หัวกลุ่มคลิกกาง/หุบได้ */
.lv-group { border-top: 1px solid var(--c-border); }
.lv-group:first-child { border-top: 0; }
.lv-group-head { display: flex; justify-content: space-between; align-items: center; padding: 9px 2px; cursor: pointer; font-size: 13px; font-weight: 700; }
.lv-group-count { font-size: 11.5px; font-weight: 400; color: var(--c-text-muted); }
.lv-group-body { padding-left: 4px; }
.lv-group-body .lv-row:last-child { border-bottom: 0; }
</style>
